import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Save, Check, History, Plus, Trash2, RotateCcw, Loader2, X, Eye } from "lucide-react";
import api from "../../lib/api";
import AIDialog from "../../components/admin/AIDialog";
import PreviewModal from "../../components/admin/PreviewModal";
import { PAGES } from "./AdminLayout";

export default function PageEditor() {
  const { pageKey } = useParams();
  const pageMeta = PAGES.find((p) => p.key === pageKey);

  const [blocks, setBlocks] = useState([]);
  const initialRef = useRef({}); // key -> initial (stringified) value for dirty check
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [savedFlash, setSavedFlash] = useState({});
  const [errorFlash, setErrorFlash] = useState({});
  const [aiOpen, setAIOpen] = useState(false);
  const [aiTarget, setAITarget] = useState(null);

  const [historyOpen, setHistoryOpen] = useState(null); // key
  const [history, setHistory] = useState([]);

  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get("/admin/content").then(({ data }) => {
      if (cancel) return;
      const filtered = (data || []).filter((b) => b.page_key === pageKey);
      filtered.sort((a, b) => a.key.localeCompare(b.key));
      const initial = {};
      for (const b of filtered) initial[b.key] = JSON.stringify(b.value);
      initialRef.current = initial;
      setBlocks(filtered);
      setLoading(false);
    });
    return () => { cancel = true; };
  }, [pageKey]);

  const updateLocal = (key, value) => {
    setBlocks((arr) => arr.map((b) => (b.key === key ? { ...b, value } : b)));
  };

  const isDirty = (key, value) =>
    initialRef.current[key] !== undefined &&
    JSON.stringify(value) !== initialRef.current[key];

  const save = async (key) => {
    setSaving((s) => ({ ...s, [key]: true }));
    setErrorFlash((s) => ({ ...s, [key]: "" }));
    const block = blocks.find((b) => b.key === key);
    try {
      await api.put("/admin/content", { key, value: block.value });
      initialRef.current[key] = JSON.stringify(block.value);
      window.dispatchEvent(new CustomEvent("neptune:content-updated"));
      setSavedFlash((s) => ({ ...s, [key]: true }));
      setTimeout(() => setSavedFlash((s) => ({ ...s, [key]: false })), 1800);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      setErrorFlash((s) => ({ ...s, [key]: msg }));
      setTimeout(() => setErrorFlash((s) => ({ ...s, [key]: "" })), 3200);
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  const openAI = (key, currentValue, setValue) => {
    setAITarget({ key, currentValue, setValue });
    setAIOpen(true);
  };

  const openHistory = async (key) => {
    setHistoryOpen(key);
    setHistory([]);
    const { data } = await api.get(`/admin/content/${encodeURIComponent(key)}/history`);
    setHistory(data || []);
  };

  const rollback = async (key, saved_at) => {
    await api.post(`/admin/content/${encodeURIComponent(key)}/rollback`, { saved_at });
    const { data } = await api.get("/admin/content");
    const filtered = (data || []).filter((b) => b.page_key === pageKey).sort((a, b) => a.key.localeCompare(b.key));
    const initial = {};
    for (const b of filtered) initial[b.key] = JSON.stringify(b.value);
    initialRef.current = initial;
    setBlocks(filtered);
    window.dispatchEvent(new CustomEvent("neptune:content-updated"));
    setHistoryOpen(null);
  };

  // Group by section
  const sections = useMemo(() => {
    const grouped = {};
    for (const b of blocks) {
      grouped[b.section_key] = grouped[b.section_key] || [];
      grouped[b.section_key].push(b);
    }
    return Object.entries(grouped);
  }, [blocks]);

  if (loading) {
    return <div className="text-zinc-500 text-[12px] uppercase tracking-[0.22em]">Loading…</div>;
  }
  if (!blocks.length) {
    return <div className="text-zinc-400 text-sm">No content for this page yet.</div>;
  }

  return (
    <div className="max-w-4xl">
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Page editor</div>
          <h1 className="font-display mt-3 text-3xl sm:text-5xl font-semibold tracking-tighter chrome-text">
            {pageMeta?.label || pageKey}
          </h1>
          <p className="mt-3 text-[14px] text-zinc-500">
            Edit any field. Changes go live the moment you save.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          data-testid="open-preview-btn"
          title="Preview unsaved changes"
          className="shrink-0 mt-1 inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] bg-white/[0.02] hover:bg-white/[0.06] text-white transition-colors px-3.5 py-2 text-[12.5px] font-medium"
        >
          <Eye className="h-3.5 w-3.5" />
          Preview
        </button>
      </div>

      <div className="mt-12 space-y-14">
        {sections.map(([sectionKey, fields], si) => (
          <motion.section
            key={sectionKey}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: Math.min(si * 0.04, 0.24) }}
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-5">
              — {sectionKey}
            </div>
            <div className="space-y-7">
              {fields.map((b) => (
                <FieldEditor
                  key={b.key}
                  block={b}
                  saving={saving[b.key]}
                  saved={savedFlash[b.key]}
                  error={errorFlash[b.key]}
                  dirty={isDirty(b.key, b.value)}
                  onChange={(v) => updateLocal(b.key, v)}
                  onSave={() => save(b.key)}
                  onAI={(currentValue, setValue) => openAI(b.key, currentValue, setValue)}
                  onHistory={() => openHistory(b.key)}
                />
              ))}
            </div>
          </motion.section>
        ))}
      </div>

      <AIDialog
        open={aiOpen}
        initial={typeof aiTarget?.currentValue === "string" ? aiTarget.currentValue : ""}
        onClose={() => setAIOpen(false)}
        onApply={(text) => {
          aiTarget?.setValue?.(text);
          setAIOpen(false);
        }}
      />

      <PreviewModal
        open={previewOpen}
        pageKey={pageKey}
        blocks={blocks}
        onClose={() => setPreviewOpen(false)}
      />

      {/* History drawer */}
      <AnimatePresence>
        {historyOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[90] flex"
            onClick={() => setHistoryOpen(null)}
            data-testid="history-drawer"
          >
            <div className="absolute inset-0 bg-black/60 backdrop-blur" />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="ml-auto relative h-full w-full max-w-md bg-[#0c0c0e] border-l border-white/[0.06] p-6 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— History</div>
                  <div className="text-white text-[14px] mt-1 font-mono break-all">{historyOpen}</div>
                </div>
                <button
                  onClick={() => setHistoryOpen(null)}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-white/10 text-zinc-300 hover:text-white hover:bg-white/[0.06] transition-colors"
                  data-testid="history-close"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="mt-7 space-y-3">
                {history.length === 0 && (
                  <div className="text-zinc-500 text-[13px]">No previous versions.</div>
                )}
                {history.map((v) => (
                  <HistoryItem
                    key={v.saved_at}
                    version={v}
                    onRollback={() => rollback(historyOpen, v.saved_at)}
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FieldEditor({ block, saving, saved, error, dirty, onChange, onSave, onAI, onHistory }) {
  const isList = block.type === "list";
  const wrapperRef = useRef(null);

  // Cmd/Ctrl+S while focused inside this field saves it
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onKey = (e) => {
      const isSave = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s";
      if (!isSave) return;
      if (!el.contains(document.activeElement)) return;
      e.preventDefault();
      if (!saving && dirty) onSave();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onSave, saving, dirty]);

  return (
    <div
      ref={wrapperRef}
      className={`rounded-2xl border bg-white/[0.015] p-5 transition-colors ${
        dirty ? "border-[#00E5FF]/25" : "border-white/[0.06]"
      }`}
      data-dirty={dirty ? "true" : "false"}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">{block.label}</div>
            {dirty && (
              <span
                title="Unsaved"
                className="inline-block h-1.5 w-1.5 rounded-full bg-[#00E5FF] shadow-[0_0_10px_rgba(0,229,255,0.7)]"
                data-testid={`dirty-dot-${block.key}`}
              />
            )}
          </div>
          <div className="font-mono text-[10.5px] text-zinc-600 mt-0.5 truncate">{block.key}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={onHistory}
            data-testid={`history-btn-${block.key}`}
            title="History"
            className="h-8 w-8 inline-flex items-center justify-center rounded-full text-zinc-400 hover:text-white hover:bg-white/[0.05] transition-colors"
          >
            <History className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onSave}
            data-testid={`save-btn-${block.key}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors px-3 py-1.5 text-[12px] font-medium"
            disabled={saving || !dirty}
            title={dirty ? "Save (⌘S)" : "No changes"}
          >
            {saving ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : saved ? (
              <Check className="h-3 w-3" />
            ) : (
              <Save className="h-3 w-3" />
            )}
            {saved ? "Saved" : "Save"}
          </button>
        </div>
      </div>

      <div className="mt-4">
        {isList ? (
          <ListEditor block={block} onChange={onChange} onAI={onAI} />
        ) : block.type === "boolean" ? (
          <BooleanEditor block={block} onChange={onChange} />
        ) : (
          <TextEditor block={block} onChange={onChange} onAI={onAI} />
        )}
      </div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="mt-3 text-[12px] text-rose-400"
            data-testid={`error-${block.key}`}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function BooleanEditor({ block, onChange }) {
  const value = Boolean(block.value);
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        data-testid={`toggle-${block.key}`}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          value ? "bg-gradient-to-r from-[#00E5FF] to-[#0055FF]" : "bg-white/[0.08] border border-white/[0.08]"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            value ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
      <span className="text-[13px] text-zinc-300">
        {value ? "Visible — shown in footer" : "Hidden — not in footer"}
      </span>
    </div>
  );
}

function TextEditor({ block, onChange, onAI }) {
  const isLong = block.type === "textarea" || (typeof block.value === "string" && block.value.length > 80);
  const Comp = isLong ? "textarea" : "input";
  return (
    <div className="relative">
      <Comp
        value={block.value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={isLong ? 3 : undefined}
        data-testid={`field-${block.key}`}
        className="w-full rounded-xl bg-white/[0.02] border border-white/[0.08] px-3.5 py-2.5 text-[14px] text-white focus:outline-none focus:border-[#00E5FF]/40 focus:bg-white/[0.04] transition-colors resize-none"
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => onAI(block.value, (v) => onChange(v))}
          data-testid={`ai-btn-${block.key}`}
          className="inline-flex items-center gap-1.5 text-[12px] text-zinc-400 hover:text-[#00E5FF] transition-colors"
        >
          <Sparkles className="h-3 w-3" /> Improve with AI
        </button>
        {typeof block.value === "string" && (
          <span className="text-[11px] text-zinc-600 tabular-nums">
            {block.value.length} ch
          </span>
        )}
      </div>
    </div>
  );
}

function ListEditor({ block, onChange, onAI }) {
  const items = Array.isArray(block.value) ? block.value : [];
  const fields = items[0] ? Object.keys(items[0]) : [];
  const [confirmIdx, setConfirmIdx] = useState(null);

  const setItem = (idx, key, value) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [key]: value } : it));
    onChange(next);
  };
  const addItem = () => {
    const next = [...items, fields.length ? Object.fromEntries(fields.map((f) => [f, ""])) : { text: "" }];
    onChange(next);
  };
  const removeItem = (idx) => {
    onChange(items.filter((_, i) => i !== idx));
    setConfirmIdx(null);
  };

  return (
    <div className="space-y-3">
      {items.map((it, idx) => (
        <div key={idx} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[11px] text-zinc-500">item {String(idx + 1).padStart(2, "0")}</span>
            {confirmIdx === idx ? (
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-zinc-400">Remove?</span>
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  className="text-[11px] text-rose-400 hover:text-rose-300"
                  data-testid={`list-remove-confirm-${block.key}-${idx}`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmIdx(null)}
                  className="text-[11px] text-zinc-500 hover:text-white"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmIdx(idx)}
                className="text-zinc-500 hover:text-rose-400"
                data-testid={`list-remove-${block.key}-${idx}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="space-y-2">
            {Object.entries(it).map(([k, v]) => (
              <ListItemField
                key={k}
                fkey={k}
                value={v}
                onChange={(nv) => setItem(idx, k, nv)}
                onAI={(currentValue, setValue) => onAI(currentValue, setValue)}
              />
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        data-testid={`list-add-${block.key}`}
        className="inline-flex items-center gap-1.5 text-[12.5px] text-zinc-400 hover:text-white border border-white/[0.08] bg-white/[0.015] hover:bg-white/[0.04] rounded-full px-3 py-1.5 transition-colors"
      >
        <Plus className="h-3.5 w-3.5" /> Add item
      </button>
    </div>
  );
}

function ListItemField({ fkey, value, onChange, onAI }) {
  if (Array.isArray(value)) {
    return (
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-600 mb-1">{fkey}</div>
        <textarea
          value={JSON.stringify(value, null, 2)}
          onChange={(e) => {
            try { onChange(JSON.parse(e.target.value)); } catch { /* ignore invalid */ }
          }}
          rows={4}
          className="w-full rounded-lg bg-white/[0.02] border border-white/[0.08] px-3 py-2 text-[12.5px] text-zinc-200 font-mono focus:outline-none focus:border-[#00E5FF]/40"
        />
      </div>
    );
  }
  const isLong = typeof value === "string" && value.length > 80;
  const Comp = isLong ? "textarea" : "input";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-600 mb-1">{fkey}</div>
      <div className="flex items-start gap-2">
        <Comp
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={isLong ? 2 : undefined}
          className="flex-1 rounded-lg bg-white/[0.02] border border-white/[0.08] px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#00E5FF]/40 resize-none"
        />
        {typeof value === "string" && (
          <button
            type="button"
            onClick={() => onAI(value, onChange)}
            title="Improve with AI"
            className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-lg text-zinc-400 hover:text-[#00E5FF] hover:bg-white/[0.04] transition-colors"
          >
            <Sparkles className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function HistoryItem({ version, onRollback }) {
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
      <div className="flex items-center justify-between text-[11px] text-zinc-500">
        <span>{new Date(version.saved_at).toLocaleString()}</span>
        <span className="truncate ml-2">{version.saved_by}</span>
      </div>
      <pre className="mt-2 text-[12.5px] text-zinc-300 whitespace-pre-wrap break-words font-body max-h-40 overflow-auto">
        {typeof version.value === "string" ? version.value : JSON.stringify(version.value, null, 2)}
      </pre>
      {confirm ? (
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={onRollback}
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 px-3 py-1.5 text-[12px] font-medium"
            data-testid="rollback-confirm-btn"
          >
            <RotateCcw className="h-3 w-3" /> Confirm rollback
          </button>
          <button
            onClick={() => setConfirm(false)}
            className="text-[12px] text-zinc-400 hover:text-white"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setConfirm(true)}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] bg-white/[0.02] text-white hover:bg-white/[0.06] px-3 py-1.5 text-[12px] font-medium transition-colors"
          data-testid="rollback-btn"
        >
          <RotateCcw className="h-3 w-3" /> Rollback
        </button>
      )}
    </div>
  );
}
